<template>
    <b-row>
        <b-col>
            <card title="Driver's License Verification Queue">
                <div v-if="items && items.length">
                    <div v-for="scan in items" :key="scan.id">
                        <card>
                            <div style="cursor:pointer" v-on:click="toggleInfo(scan)"
                                v-b-toggle="'panel-' + scan.id.toString()">
                                <h6>
                                    {{ scan.patientName }}
                                    <b-badge v-if="scan.parseSuccess" variant="success" class="ml-2">
                                        <i class="fas fa-barcode"></i> Barcode Parsed
                                    </b-badge>
                                    <b-badge v-else-if="scan.parseSuccess === false" variant="warning" class="ml-2">
                                        <i class="fas fa-exclamation-triangle"></i> Parse Failed
                                    </b-badge>
                                </h6>
                                <span>Uploaded {{ getAge(scan.uploaded) }}</span>
                            </div>

                            <b-collapse accordion :id="'panel-' + scan.id.toString()"
                                :ref="'panel-' + scan.id.toString()">
                                <hr style="border-width:3px" />

                                <b-row>
                                    <b-col cols="6">
                                        <!-- PATIENT INFORMATION -->
                                        <b-row>
                                            <b-col cols="4">
                                                <b>Date Of Birth: </b>{{ convertDate(scan.patientDOB) }} <br />
                                                <b>Phone: </b>{{ scan.phoneNumber | formatPhone }}<br />
                                                <b>Cell: </b>{{ scan.cellNumber | formatPhone }}
                                            </b-col>
                                            <b-col cols="4">
                                                {{ scan.patientAddress1 }} <br />
                                                {{ scan.patientAddress2 }}<br v-if="scan.patientAddress2?.length > 0" />
                                                {{ scan.patientAddressCity }}, {{ scan.patientAddressState }}
                                            </b-col>
                                            <b-col>
                                                <router-link v-if="scan.patKey" target="_blank"
                                                    :to="{ name: 'PatientDashboard', params: { id: scan.patKey } }">
                                                    <span>Go To Profile</span> <i class="fas fa-user"></i>
                                                </router-link>
                                            </b-col>
                                        </b-row>
                                        <hr />
                                        <!-- DRIVER'S LICENSE IMAGES -->
                                        <b-row>
                                            <b-col>
                                                <div style="width:500px;height:600px;display:block;">
                                                    <viewer v-if="currentPatientId == scan.patKey" class="viewer"
                                                        :id="'viewer-' + scan.id.toString()"
                                                        :ref="'viewer-' + scan.id.toString()" :options="viewerOptions()"
                                                        :images="scan.imageURLs" style="width:500px;height:600px;">
                                                        <template #default="scope">
                                                            <img v-for="src in scope.images" :src="src" :key="src"
                                                                :alt="src" class="d-none">
                                                        </template>
                                                    </viewer>
                                                </div>
                                                <h4 style="text-align:center"
                                                    v-if="!scan.imageURLs || scan.imageURLs.length == 0">NO IMAGES
                                                    UPLOADED</h4>
                                            </b-col>
                                        </b-row>

                                        <b-button variant="success" @click="processScan(scan)">Processed</b-button>
                                        <b-button variant="danger" class="ml-2" @click="rejectScan(scan)">Reject</b-button>
                                    </b-col>
                                    <b-col cols="6">
                                        <!-- DRIVER'S LICENSE DATA -->
                                        <card plain class="mt-3">
                                            <h5><i class="fas fa-id-card"></i> Driver's License Data</h5>
                                            <b-row>
                                                <b-col cols="6">
                                                    <b>License Number: </b>
                                                    <b-form-input v-model="scan.editLicenseNumber" />
                                                </b-col>
                                                <b-col cols="6">
                                                    <b>Issuing State: </b>
                                                    <b-form-input v-model="scan.editLicenseState" />
                                                </b-col>
                                            </b-row>
                                            <b-row class="mt-2">
                                                <b-col cols="6">
                                                    <b>Expiration Date: </b>
                                                    <b-form-input type="date" v-model="scan.editExpirationDate" :state="scan.editExpirationDate ? null : false" />
                                                    <b-form-invalid-feedback :state="scan.editExpirationDate ? null : false">Expiration date is required to process.</b-form-invalid-feedback>
                                                </b-col>
                                                <b-col cols="6" v-if="getParsedData(scan)">
                                                    <b>Issue Date: </b> <span>{{ convertDate(getParsedData(scan).issueDate) }}</span>
                                                </b-col>
                                            </b-row>
                                            <div v-if="getParsedData(scan)">
                                                <hr>
                                                <h6>Personal Information</h6>
                                                <b-row>
                                                    <b-col cols="4">
                                                        <b>First Name: </b> <span>{{ getParsedData(scan).firstName || 'N/A' }}</span>
                                                    </b-col>
                                                    <b-col cols="4">
                                                        <b>Middle Name: </b> <span>{{ getParsedData(scan).middleName || 'N/A' }}</span>
                                                    </b-col>
                                                    <b-col cols="4">
                                                        <b>Last Name: </b> <span>{{ getParsedData(scan).lastName || 'N/A' }}</span>
                                                    </b-col>
                                                </b-row>
                                                <b-row class="mt-2">
                                                    <b-col cols="4">
                                                        <b>Date of Birth: </b> <span>{{ convertDate(getParsedData(scan).dateOfBirth) }}</span>
                                                    </b-col>
                                                    <b-col cols="4">
                                                        <b>Sex: </b> <span>{{ getParsedData(scan).sex || 'N/A' }}</span>
                                                    </b-col>
                                                    <b-col cols="4">
                                                        <b>Eye Color: </b> <span>{{ getParsedData(scan).eyeColor || 'N/A' }}</span>
                                                    </b-col>
                                                </b-row>
                                                <b-row class="mt-2">
                                                    <b-col cols="4">
                                                        <b>Height: </b> <span>{{ getParsedData(scan).height || 'N/A' }}</span>
                                                    </b-col>
                                                    <b-col cols="4">
                                                        <b>Weight: </b> <span>{{ getParsedData(scan).weight || 'N/A' }}</span>
                                                    </b-col>
                                                </b-row>
                                                <hr>
                                                <h6>Address on License</h6>
                                                <b-row>
                                                    <b-col cols="12">
                                                        <span>{{ getParsedData(scan).streetLine1 || '' }}</span><br v-if="getParsedData(scan).streetLine1">
                                                        <span v-if="getParsedData(scan).streetLine2">{{ getParsedData(scan).streetLine2 }}<br></span>
                                                        <span>{{ getParsedData(scan).city || '' }}, {{ getParsedData(scan).jurisdictionCode || '' }} {{ getParsedData(scan).postalCode || '' }}</span>
                                                    </b-col>
                                                </b-row>
                                            </div>
                                            <div v-else>
                                                <p class="text-muted mt-2">No barcode data was extracted from the uploaded images. Enter the license details manually.</p>
                                            </div>
                                        </card>
                                    </b-col>
                                </b-row>

                            </b-collapse>
                        </card>
                    </div>
                </div>
                <div v-else>
                    No Driver's License verifications are pending.
                </div>
            </card>
        </b-col>
    </b-row>

</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { PatientDriversLicenseScan } from '@/models/PatientDriversLicenseScan';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import 'viewerjs/dist/viewer.css';
import { component as Viewer } from 'v-viewer';

@Component({
    name: "PatientDriversLicenseQueue",
    components: {
        Viewer
    },
})

export default class PatientDriversLicenseQueue extends Vue {
    private items: Array<PatientDriversLicenseScan> = [];
    private currentPatientId: number | string = "";

    viewerOptions() {
        return {
            inline: true,
            debug: true,
            button: false,
            title: false,
            transition: false,
            toolbar: {
                zoomIn: 1,
                zoomOut: 1,
                reset: 1,
                prev: 1,
                next: 1,
                rotateLeft: 1,
                rotateRight: 1,
                flipHorizontal: 1,
                flipVertical: 1,
                oneToOne: 0,
                play: {
                    show: 0,
                    size: 'large'
                }
            }
        };
    }

    async created() {
        await this.loadData();
    }

    async loadData() {
        try {
            const response = await this.$http.get<Array<PatientDriversLicenseScan>>(`/PatientDriversLicense/Unprocessed`);
            this.items = response.data.map(scan => {
                const parsed = this.getParsedData(scan);
                scan.editLicenseNumber = parsed?.idNumber ?? "";
                scan.editLicenseState = parsed?.jurisdictionCode ?? "";
                scan.editExpirationDate = parsed?.expirationDate ? moment(parsed.expirationDate).format("YYYY-MM-DD") : "";
                return scan;
            });
        } catch (error) {
            console.error("Error while loading driver's license scans.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    convertDate(date: string | Date) {
        if (!date) return 'N/A';
        return moment(date).local().format("L");
    }

    getAge(date: Date) {
        return moment.utc(date).fromNow()
    }

    toggleInfo(scan: PatientDriversLicenseScan) {
        this.items.forEach(item => {
            const panelRef = this.$refs['panel-' + item.id] as any;
            if (panelRef && panelRef[0]) {
                panelRef[0].show = false;
            }
        });

        this.currentPatientId = scan.patKey;

        const viewerRef = this.$refs[`viewer-${scan.id}`] as any;
        if (viewerRef && viewerRef[0] && viewerRef[0].rebuildViewer) {
            viewerRef[0].rebuildViewer();
        }
    }

    getParsedData(scan: PatientDriversLicenseScan): any | null {
        if (!scan.parsedData) return null;
        try {
            return JSON.parse(scan.parsedData);
        } catch (e) {
            console.error("Failed to parse driver's license data:", e);
            return null;
        }
    }

    async processScan(scan: PatientDriversLicenseScan) {
        if (!scan.editExpirationDate) {
            this.$notification(NotificationOptions.warning("An expiration date is required to process a driver's license."));
            return;
        }
        try {
            await this.$http.post(`/PatientDriversLicense/Process/${scan.id}`, {
                licenseNumber: scan.editLicenseNumber,
                licenseState: scan.editLicenseState,
                licenseExpirationDate: scan.editExpirationDate || null
            });
            this.$notification(NotificationOptions.notificationOptionsPreset("Processed Successfully", NotificationOptions.NotificationTypes.success));
            await this.loadData();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Error while processing driver's license scan", {
                error,
                response: error?.response
            });
        }
    }

    async rejectScan(scan: PatientDriversLicenseScan) {
        const confirmed = await this.$bvModal.msgBoxConfirm("Reject this driver's license scan? The patient will need to re-upload.", {
            okVariant: "danger",
            okTitle: "Reject"
        });
        if (!confirmed) return;
        try {
            await this.$http.post(`/PatientDriversLicense/Reject/${scan.id}`);
            this.$notification(NotificationOptions.notificationOptionsPreset("Rejected Successfully", NotificationOptions.NotificationTypes.success));
            await this.loadData();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Error while rejecting driver's license scan", {
                error,
                response: error?.response
            });
        }
    }
}
</script>
